import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { experiences, getTypeColor, getTypeLabel } from "../data/experiences";

const Experience: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut" as const,
      },
    },
  };

  return (
    <section
      id="experience"
      className="py-20 bg-gradient-to-br from-secondary-50 via-white to-blue-50 dark:from-secondary-900 dark:via-secondary-800 dark:to-blue-900/20 relative overflow-hidden"
    >
      {/* Background decorations */}
      <div className="absolute inset-0">
        <div className="absolute top-10 right-10 w-32 h-32 bg-primary-200 dark:bg-primary-600 rounded-full opacity-20 animate-float"></div>
        <div
          className="absolute bottom-10 left-10 w-40 h-40 bg-blue-200 dark:bg-blue-600 rounded-full opacity-20 animate-float"
          style={{ animationDelay: "2s" }}
        ></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="max-w-6xl mx-auto"
        >
          {/* Header */}
          <motion.div variants={itemVariants} className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6 bg-gradient-to-r from-gray-900 via-primary-600 to-blue-600 dark:from-white dark:via-primary-400 dark:to-blue-400 bg-clip-text text-transparent">
              Expériences
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-primary-600 to-blue-600 dark:from-primary-400 dark:to-blue-400 mx-auto rounded-full mb-8"></div>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Mon parcours professionnel et mes réalisations
            </p>
          </motion.div>

          {/* Timeline */}
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-4 md:left-1/2 transform md:-translate-x-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary-500 to-blue-500"></div>

            <div className="space-y-12">
              {experiences.map((experience, index) => (
                <motion.div
                  key={experience.id}
                  variants={itemVariants}
                  className={`relative flex items-center ${
                    index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                  }`}
                >
                  {/* Timeline dot */}
                  <div className="absolute left-4 md:left-1/2 transform md:-translate-x-1/2 w-4 h-4 bg-primary-500 rounded-full border-4 border-white dark:border-secondary-900 shadow-soft"></div>

                  {/* Content */}
                  <div
                    className={`ml-12 md:ml-0 md:w-5/12 ${
                      index % 2 === 0 ? "md:pr-8" : "md:pl-8"
                    }`}
                  >
                    <motion.div
                      whileHover={{ scale: 1.02, y: -2 }}
                      className="bg-white/80 dark:bg-secondary-800/80 backdrop-blur-sm rounded-2xl p-6 shadow-soft dark:shadow-dark-soft border border-white/20 dark:border-secondary-700/20 hover:border-primary-200 dark:hover:border-primary-600/50 transition-all duration-300"
                    >
                      {/* Type badge */}
                      <div className="flex items-center gap-2 mb-4">
                        <span
                          className={`px-3 py-1 text-xs font-semibold rounded-full ${getTypeColor(
                            experience.type
                          )}`}
                        >
                          {getTypeLabel(experience.type)}
                        </span>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {experience.period}
                        </span>
                      </div>

                      {/* Title and company */}
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                        {experience.title}
                      </h3>
                      <div className="flex items-center gap-2 mb-4">
                        <span className="text-lg font-semibold text-primary-600 dark:text-primary-400">
                          {experience.company}
                        </span>
                        {experience.location && (
                          <>
                            <span className="text-gray-400">•</span>
                            <span className="text-gray-600 dark:text-gray-400">
                              {experience.location}
                            </span>
                          </>
                        )}
                      </div>

                      {/* Description */}
                      <p className="text-gray-600 dark:text-gray-300 mb-4 leading-relaxed">
                        {experience.description}
                      </p>

                      {/* Technologies */}
                      {experience.technologies &&
                        experience.technologies.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-4">
                            {experience.technologies.map((tech, techIndex) => (
                              <span
                                key={techIndex}
                                className="px-2 py-1 text-xs font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full border border-primary-200 dark:border-primary-700/30"
                              >
                                {tech}
                              </span>
                            ))}
                          </div>
                        )}

                      {/* Achievements */}
                      {experience.achievements &&
                        experience.achievements.length > 0 && (
                          <div className="space-y-2">
                            <h4 className="font-semibold text-gray-900 dark:text-white text-sm">
                              Réalisations principales :
                            </h4>
                            <ul className="space-y-1">
                              {experience.achievements.map(
                                (achievement, achievementIndex) => (
                                  <li
                                    key={achievementIndex}
                                    className="text-sm text-gray-600 dark:text-gray-400 flex items-start gap-2"
                                  >
                                    <span className="text-primary-500 mt-1">
                                      •
                                    </span>
                                    {achievement}
                                  </li>
                                )
                              )}
                            </ul>
                          </div>
                        )}
                    </motion.div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Call to action */}
          <motion.div variants={itemVariants} className="text-center mt-16">
            <motion.a
              href="/CV/CV-JPB.pdf"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-3 bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-800 dark:to-gray-600 text-white font-semibold py-4 px-8 rounded-full text-lg transition-all duration-300 shadow-soft dark:shadow-dark-soft hover:shadow-glow dark:hover:shadow-dark-glow"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              Télécharger mon CV
            </motion.a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Experience;
